/**
 * usePushSubscription — encapsulates the browser Push API dance.
 *
 * States:
 *   - `supported`: browser has serviceWorker + PushManager + Notification.
 *   - `permission`: 'default' | 'granted' | 'denied' (mirrors Notification.permission).
 *   - `subscribed`: whether the SW currently holds a push subscription tied to
 *     THIS browser (checked via getSubscription()).
 *   - `loading`: async op in flight.
 *
 * Actions:
 *   - `enable()`  → request permission, subscribe, POST subscription to backend.
 *   - `disable()` → tell backend to forget the subscription, then unsubscribe.
 *   - `test()`    → hit /push/test to send one to yourself.
 */
import { useCallback, useEffect, useState } from 'react';
import { pushApi } from '../lib/api';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) output[i] = rawData.charCodeAt(i);
  return output;
}

const isSupported = () =>
  typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window
  && window.isSecureContext;

export default function usePushSubscription() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!isSupported()) {
      setSupported(false);
      return;
    }
    setSupported(true);
    setPermission(Notification.permission);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    } catch (e) {
      setSubscribed(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const enable = useCallback(async () => {
    setError(null);
    if (!isSupported()) {
      setError('Push non supporté par ce navigateur');
      return { ok: false };
    }
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setError('Permission refusée');
        return { ok: false };
      }
      // Ensure the SW is actually registered — a fresh browser + preview env
      // may hit this hook before the on-load registration fires.
      let reg;
      try {
        reg = await navigator.serviceWorker.getRegistration();
        if (!reg) {
          reg = await navigator.serviceWorker.register('/service-worker.js');
        }
        reg = await navigator.serviceWorker.ready;
      } catch (regErr) {
        console.error('[push] SW registration failed', regErr);
        setError('Service Worker introuvable — rechargez la page');
        return { ok: false, error: regErr };
      }
      let sub;
      try {
        sub = await reg.pushManager.getSubscription();
      } catch (_) { sub = null; }
      if (!sub) {
        const { data } = await pushApi.vapidKey();
        if (!data?.publicKey) {
          setError('VAPID public key manquante côté serveur');
          return { ok: false };
        }
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
      }
      await pushApi.subscribe(sub.toJSON());
      setSubscribed(true);
      return { ok: true };
    } catch (e) {
      console.error('[push] enable failed', e);
      const msg = e?.message || e?.response?.data?.detail || 'Échec activation';
      setError(msg);
      return { ok: false, error: e };
    } finally {
      setLoading(false);
    }
  }, []);

  const disable = useCallback(async () => {
    setError(null);
    if (!isSupported()) return { ok: false };
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try {
          await pushApi.unsubscribe(sub.endpoint);
        } catch (_) { /* ignore */ }
        await sub.unsubscribe();
      }
      setSubscribed(false);
      return { ok: true };
    } catch (e) {
      setError(e?.message || 'Échec désactivation');
      return { ok: false, error: e };
    } finally {
      setLoading(false);
    }
  }, []);

  const test = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const { data } = await pushApi.test();
      return { ok: true, data };
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Échec envoi test');
      return { ok: false, error: e };
    } finally {
      setLoading(false);
    }
  }, []);

  return { supported, permission, subscribed, loading, error, enable, disable, test, refresh };
}
